import Vue from 'vue';
import store from '@/store';
import platform from 'platform';
import { Logger } from 'aws-amplify';
const logger = new Logger('Capabilities');
const version = '1.0.0';

const p240 = {
  type: 'file',
  video: {
    contentType: 'video/mp4;codecs=avc1.640028',
    width: 480,
    height: 240,
    bitrate: 350000,
    framerate: 29.97,
  },
  audio: {
    contentType: 'audio/mp4;codecs=mp4a.40.2',
    channels: 2,
    bitrate: 160000,
    samplerate: 48000,
  },
};

const p360 = {
  type: 'file',
  video: {
    contentType: 'video/mp4;codecs=avc1.640028',
    width: 640,
    height: 320,
    bitrate: 700000,
    framerate: 29.97,
  },
  audio: {
    contentType: 'audio/mp4;codecs=mp4a.40.2',
    channels: 2,
    bitrate: 160000,
    samplerate: 48000,
  },
};

const p480 = {
  type: 'file',
  video: {
    contentType: 'video/mp4;codecs=avc1.640028',
    width: 960,
    height: 480,
    bitrate: 1000000,
    framerate: 29.97,
  },
  audio: {
    contentType: 'audio/mp4;codecs=mp4a.40.2',
    channels: 2,
    bitrate: 160000,
    samplerate: 48000,
  },
};

const p720 = {
  type: 'file',
  video: {
    contentType: 'video/mp4;codecs=avc1.640028',
    width: 1280,
    height: 640,
    bitrate: 2000000,
    framerate: 29.97,
  },
  audio: {
    contentType: 'audio/mp4;codecs=mp4a.40.2',
    channels: 2,
    bitrate: 160000,
    samplerate: 48000,
  },
};

const p1080 = {
  type: 'file',
  video: {
    contentType: 'video/mp4;codecs=avc1.640028',
    width: 1920,
    height: 960,
    bitrate: 6000000,
    framerate: 29.97,
  },
  audio: {
    contentType: 'audio/mp4;codecs=mp4a.40.2',
    channels: 2,
    bitrate: 160000,
    samplerate: 48000,
  },
};

const p1440 = {
  type: 'file',
  video: {
    contentType: 'video/mp4;codecs=avc1.640033',
    width: 2560,
    height: 1280,
    bitrate: 8000000,
    framerate: 29.97,
  },
  audio: {
    contentType: 'audio/mp4;codecs=mp4a.40.2',
    channels: 2,
    bitrate: 160000,
    samplerate: 48000,
  },
};

const p2160 = {
  type: 'file',
  video: {
    contentType: 'video/mp4;codecs=avc1.640033',
    width: 3840,
    height: 1920,
    bitrate: 12000000,
    framerate: 29.97,
  },
  audio: {
    contentType: 'audio/mp4;codecs=mp4a.40.2',
    channels: 2,
    bitrate: 160000,
    samplerate: 48000,
  },
};


/* Check browser info using 'platform' library */
const browserInfo = () => {
  const {
    description,
    layout,
    manufacturer,
    name,
    ua,
    version,
    os,
  } = platform;
  return {
    description,
    layout,
    manufacturer,
    name,
    ua,
    version,
    os,
  };
};

/* Check media playback capabilities using Media Capabilities browser API */
const capabilitiesInfo = async () => {
  if ('mediaCapabilities' in navigator) {
    // mediaCapabilities is available
    const resolutions = new Map();
    resolutions.set('240p', p240);
    resolutions.set('360p', p360);
    resolutions.set('480p', p480);
    resolutions.set('720p', p720);
    resolutions.set('1080p', p1080);
    resolutions.set('1440p', p1440);
    resolutions.set('2160p', p2160);

    const capabilities = {};
    for (const [key, value] of resolutions) {
      try {
        capabilities[key] = await navigator.mediaCapabilities.decodingInfo(value);
      } catch (error) {
        capabilities[key] = error;
      }
    }
    return capabilities;
  }
  // mediaCapabilities IS NOT available
  logger.error('MediaCapabilities API not available');
  return {};
};

/* Check WebGL support in browser */
const webglInfo = () => {
  const canvas = document.createElement('canvas');
  let gl = null;
  let vendor = null;
  let renderer = null;
  try {
    gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (gl && gl instanceof WebGLRenderingContext) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
        renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
      }
    }
    return { WebGL: !!gl, vendor, renderer };
  } catch (error) {
    logger.error(error);
    return {};
  }
};

const capabilities = async (projectId) => {
  const mediaCapabilities = await capabilitiesInfo();
  const unsupported = [];
  for (const [key, value] of Object.entries(mediaCapabilities)) {
    if (value.supported !== true || value.smooth !== true /*|| value.powerEfficient !== true */) {
      unsupported.push(key);
    }
  }

  const info = {
    version,
    projectId,
    mediaCapabilities,
    browser: browserInfo(),
    webGL: webglInfo(),
  };
  store.commit('unsupported', unsupported);
  try {
    await Vue.prototype.$metrics.send(info);
  } catch (err) {
    logger.error(err);
  }
};

export { capabilities as default };
